import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EventBusEventEnum } from '../enums/EventBusEventEnum';
import { UserRoleEnum } from '../enums/UserRoleEnum';
import DeviceInfo from '../interfaces/DeviceInfo';
import User from '../interfaces/User';
import EventBus from '../services/EventBus';
import { UserService } from '../services/userService';
import { clearAllCookies } from '../utils/cookie';
import { generateDeviceInfo } from '../utils/deviceInfo';
import {
  clearStorage,
  getStorageItem,
  storeStorageItem,
} from '../utils/storage';
import { StorageItemKeyEnum } from '../enums/StorageItemKeyEnum';
import { Spin } from 'antd';
import { useModal } from '../hooks/useModal';
import { useTranslation } from 'react-i18next';
import { FilesTableEnum } from '../enums/FilesTableEnum';

interface AuthContextType {
  isAdmin: boolean;
  isLoggedIn: boolean;
  isContributor: boolean;
  isExternal: boolean;
  isAdminOrContributor: boolean;
  isReader: boolean;
  token: string | null;
  authenticatedUser: User | null;
  logout: () => void;
  redirectLoggedInUser: () => void;
  login: (user: User, redirectHome: boolean) => Promise<void>;
  onBehalfOfUser: User | null;
  setOnBehalfOfUser: (user: User) => void;
  isOnBehalfOfNullOrAdmin: boolean;
  deviceInfo: DeviceInfo | null;
  isOnBehalfOfUser: boolean;
  handleOnBehalfOfUserChange: (user: User | null) => void;
  isInFileRoute: (route: FilesTableEnum) => boolean;
  onBehalfOfUserChanged: (callback: (user: User | null) => void) => void;
  offBehalfOfUserChanged: (callback: (user: User | null) => void) => void;
}

export const AuthContext = createContext<AuthContextType>({
  deviceInfo: null,
  isAdmin: false,
  isAdminOrContributor: false,
  isContributor: false,
  isExternal: false,
  isLoggedIn: false,
  isOnBehalfOfNullOrAdmin: false,
  isReader: false,
  login: async () => {},
  logout: () => {},
  onBehalfOfUser: null,
  redirectLoggedInUser: () => {},
  setOnBehalfOfUser: () => {},
  token: null,
  authenticatedUser: null,
  isOnBehalfOfUser: false,
  handleOnBehalfOfUserChange: () => {},
  isInFileRoute: () => false,
  onBehalfOfUserChanged: () => {},
  offBehalfOfUserChanged: () => {},
});

const userService = new UserService();
const deviceInfo = generateDeviceInfo();

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const [appUser, setAppUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [onBehalfOfUser, setOnBehalfOfUser] = useState<User | null>(null);
  const [isLoadingAuthenticatedUser, setIsLoadingAuthenticatedUser] =
    useState<boolean>(true);
  const onBehalfOfUserChangedSubscribers = useRef(
    new Set<(user: User | null) => void>()
  );
  const modal = useModal();

  /**
   * Logs the user out of the application by performing the following actions:
   * - Clears the current user information from the application state.
   * - Resets the authentication token.
   * - Removes all stored cookies related to the session.
   * - Clears other stored data.
   * - Redirects the user to the login page.
   *
   * This function ensures that the user's session is fully terminated and they
   * are safely navigated back to the login screen.
   */
  const logout = (): void => {
    setAppUser(null);
    setToken(null);
    clearAllCookies();
    clearStorage();
    navigate('/login');
  };

  /**
   * Asynchronously logs in a user by storing their token, updating the app's state,
   * and optionally redirecting to a home page based on the user's role.
   *
   * @param {User} user - The user object containing authentication and role information.
   * @param {boolean} [redirectHome=false] - Indicates whether the user should be redirected to the home page after login.
   * @returns {Promise<void>}
   */
  const login = async (
    user: User,
    redirectHome: boolean = false
  ): Promise<void> => {
    await storeStorageItem(
      'localStorage',
      StorageItemKeyEnum.Token,
      user.token
    );
    setToken(user.token || null);

    await storeStorageItem('localStorage', StorageItemKeyEnum.AppUser, user);
    setAppUser(user);

    if (redirectHome) {
      switch (user.role) {
        case UserRoleEnum.Reader:
        case UserRoleEnum.External:
          navigate('/files/received', { replace: true });
          break;
        default:
          navigate('/files/personal', { replace: true });
          break;
      }
    }
  };

  /**
   * Registers a callback function to be called when the "on behalf of user" state changes.
   *
   * @param {function} callback - The callback function to be called when the "on behalf of user" state changes. This function should accept a user object of type User or null.
   */
  const onBehalfOfUserChanged = (callback: (user: User | null) => void) => {
    onBehalfOfUserChangedSubscribers.current.add(callback);
  };

  /**
   * Removes a previously registered callback for the "on behalf of user" state change.
   *
   * @param {function} callback - The callback function to be removed. This function should accept a user object of type User or null.
   */
  const offBehalfOfUserChanged = (callback: (user: User | null) => void) => {
    onBehalfOfUserChangedSubscribers.current.delete(callback);
  };

  /**
   * A callback function to handle changes to the "on behalf of user" state.
   * Updates the state with the provided user object or null if no user is provided.
   *
   * @param {User | null} user - The user object to set as the "on behalf of user" state, or null to reset it.
   * @returns {void}
   */
  const handleOnBehalfOfUserChange = useCallback((user: User | null) => {
    if (user) {
      modal
        .open({
          title: t('modal.enterOnBehalfOf.title'),
          message: t('modal.enterOnBehalfOf.description', {
            resource: `${user.firstName} ${user.lastName}`,
          }),
        })
        .then(() => {
          setOnBehalfOfUser(user);
          onBehalfOfUserChangedSubscribers.current.forEach((callback) =>
            callback(user)
          );
        });
    } else {
      setOnBehalfOfUser(null);
      onBehalfOfUserChangedSubscribers.current.forEach((callback) =>
        callback(null)
      );
    }
  }, []);

  /**
   * Redirects a logged-in user to a specific route based on their role.
   *
   * This function checks if the user is logged in and if the application user data is available.
   * Depending on the role of the `appUser`, it navigates the user to a different endpoint:
   * - For roles `Reader` and `External`, the user is redirected to `/files?list=0&table=0&type=1`.
   * - For any other roles, the user is redirected to `/files?list=0&table=0&type=2`.
   *
   * The navigation replaces the current history entry to prevent the user from navigating back to the previous screen.
   *
   * Preconditions:
   * - `isLoggedIn` should be a boolean indicating if the user is logged in.
   * - `appUser` should contain user data, including the `role` property.
   * - `navigate` should be a function capable of handling navigation.
   * - `UserRoleEnum` should be an enumeration of possible user roles.
   */
  const redirectLoggedInUser = () => {
    if (isLoggedIn && appUser) {
      switch (appUser.role) {
        case UserRoleEnum.Reader:
        case UserRoleEnum.External:
          navigate(`/files/${FilesTableEnum.Received}`, { replace: true });
          break;
        default:
          navigate(`/files/${FilesTableEnum.Personal}`, { replace: true });
          break;
      }
    }
  };

  /**
   * A function to refresh and update the user's information.
   *
   * This function retrieves the latest user information using a service, updates the user data,
   * and handles potential changes to the user token. If an error occurs during the process,
   * it logs the error and logs out the user.
   *
   * Dependencies:
   * - `token`: Checks if the user is authenticated before attempting to refresh user information.
   * - `userService.getUserInfo`: Fetches the updated information for the user.
   * - `login`: Updates user's session and data with the retrieved user information.
   * - `logout`: Logs the user out in case of an error or failure to fetch user data.
   *
   * Usage considerations:
   * - Ensure `token` is readily available to authorize the request.
   * - Handle side effects such as token updates or session expiration scenarios.
   * - The dependent `token` ensures the function remains in sync with the latest token state.
   */
  const refreshUserInfo = useCallback(async () => {
    if (!token) return;
    try {
      const userInfo = await userService.getUserInfo();
      await login(userInfo, false);
    } catch (error) {
      console.error(
        "Errore nel recupero delle informazioni dell'utente",
        error
      );
      logout();
    }
  }, [token]);

  /**
   * Determines if the current location pathname includes a specific file route.
   *
   * @param {FilesTableEnum} route - The file route to check for in the current pathname.
   * @returns {boolean} True if the pathname includes the specified route, false otherwise.
   */
  const isInFileRoute = useCallback(
    (route: FilesTableEnum) => {
      return location.pathname.includes(route);
    },
    [location.pathname]
  );

  /**
   * Determines if the user is logged in based on the presence of a token.
   *
   * @returns {boolean} True if the user is logged in, false otherwise.
   */
  const isLoggedIn = useMemo(() => !!token, [token]);

  /**
   * Determines if the user is an admin based on their role.
   *
   * @returns {boolean} True if the user is an admin, false otherwise.
   */
  const isAdmin = useMemo(
    () => appUser?.role === UserRoleEnum.Admin,
    [appUser]
  );

  /**
   * Determines if the user is a contributor based on their role.
   *
   * @returns {boolean} True if the user is a contributor, false otherwise.
   */
  const isContributor = useMemo(
    () => appUser?.role === UserRoleEnum.Contributor,
    [appUser]
  );

  /**
   * Determines if the user is a reader based on their role.
   *
   * @returns {boolean} True if the user is a reader, false otherwise.
   */
  const isReader = useMemo(
    () => appUser?.role === UserRoleEnum.Reader,
    [appUser]
  );

  /**
   * Determines if the user is an external user based on their role.
   *
   * @returns {boolean} True if the user is an external user, false otherwise.
   */
  const isExternal = useMemo(
    () => appUser?.role === UserRoleEnum.External,
    [appUser]
  );

  /**
   * Determines if the user is an admin or a contributor based on their role.
   *
   * @returns {boolean} True if the user is an admin or a contributor, false otherwise.
   */
  const isAdminOrContributor = useMemo(
    () => isAdmin || isContributor,
    [isAdmin, isContributor]
  );

  /**
   * Determines if the user is on behalf of a null or admin user.
   *
   * @returns {boolean} True if the user is on behalf of a null or admin user, false otherwise.
   */
  const isOnBehalfOfNullOrAdmin = useMemo(
    () => isAdmin || !onBehalfOfUser,
    [isAdmin, onBehalfOfUser]
  );

  /**
   * Determines if the user is on behalf of a user.
   *
   * @returns {boolean} True if the user is on behalf of a user, false otherwise.
   */
  const isOnBehalfOfUserFlag = useMemo(
    () => !!onBehalfOfUser,
    [onBehalfOfUser]
  );

  useEffect(() => {
    (async () => {
      const storedOnBehalfOfUser = await getStorageItem(
        'sessionStorage',
        StorageItemKeyEnum.onBehalfOfUser
      );
      if (storedOnBehalfOfUser) {
        setOnBehalfOfUser(storedOnBehalfOfUser);
      }
    })();
  }, []);

  useEffect(() => {
    storeStorageItem(
      'sessionStorage',
      StorageItemKeyEnum.onBehalfOfUser,
      onBehalfOfUser
    );
  }, [onBehalfOfUser]);

  useEffect(() => {
    (async () => {
      const storedToken = await getStorageItem(
        'localStorage',
        StorageItemKeyEnum.Token
      );
      const storedAppUser = await getStorageItem(
        'localStorage',
        StorageItemKeyEnum.AppUser
      );
      setToken(storedToken);
      setAppUser(storedAppUser);
      setIsLoadingAuthenticatedUser(false);
    })();
  }, [location.pathname]);

  useEffect(() => {
    if (token) {
      refreshUserInfo().then(() => {});
    }
  }, [token, refreshUserInfo]);

  useEffect(() => {
    const handler = () => {
      logout();
    };
    EventBus.on(EventBusEventEnum.MakeLogout, handler);
    return () => {
      EventBus.off(EventBusEventEnum.MakeLogout, handler);
    };
  }, []);

  if (isLoadingAuthenticatedUser) {
    return <Spin spinning fullscreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        deviceInfo,
        isAdmin,
        isAdminOrContributor,
        isContributor,
        isExternal,
        isLoggedIn,
        isOnBehalfOfNullOrAdmin,
        isReader,
        login,
        logout,
        onBehalfOfUser,
        redirectLoggedInUser,
        setOnBehalfOfUser,
        token,
        authenticatedUser: appUser,
        isOnBehalfOfUser: isOnBehalfOfUserFlag,
        handleOnBehalfOfUserChange,
        isInFileRoute,
        onBehalfOfUserChanged,
        offBehalfOfUserChanged,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
